/**
 * ReportsWizardV2 — consulting-grade progress report builder.
 *
 * Two-level section picker (Module → SubModule) driven by
 * GET /v1/reports/section-catalog, per-section AI-narrative toggle, cover
 * photo + client logo upload, and a "Generate Narrated HTML" primary CTA.
 * PPTX button stays for internal-review parity.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, Sparkles, ImageIcon, Upload, ChevronDown, ChevronRight, Wand2, Loader2, Download, ExternalLink, RefreshCw, Settings2, LayoutGrid, Briefcase, Calendar, DollarSign, ShieldAlert, AlertTriangle, Camera, Wrench, Ruler, Package, FileSignature, Boxes, ScanLine, Handshake, FolderKanban, Bot, PenTool, Edit3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REPORT_TYPES = [
  { v: 'DPR', label: 'Daily · 1-Pager' },
  { v: 'WPR', label: 'Weekly · 8-12 pages' },
  { v: 'MPR', label: 'Monthly · 25-40 pages' },
  { v: 'EXEC', label: 'Executive · Board pack' },
];

export default function ReportsWizardV2() {
  const { projectId, projectName } = useProject();
  const [type, setType] = useState('WPR');
  const [meta, setMeta] = useState({ title: '', client: '', author: '', startDate: '', endDate: '' });
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState({}); // { [sectionId]: { enabled, subModules: {id: bool}, aiEnabled } }
  const [expanded, setExpanded] = useState({});
  const [reportId, setReportId] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [genJob, setGenJob] = useState(null); // { job_id, status, percent, step }
  const [docxBusy, setDocxBusy] = useState(false);
  const [generatedDocx, setGeneratedDocx] = useState(null);
  const [generatedHtml, setGeneratedHtml] = useState(null);
  const [dataHealth, setDataHealth] = useState({});
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [activePresetName, setActivePresetName] = useState(''); // shows which preset is currently loaded
  const [lastAuthoredAt, setLastAuthoredAt] = useState(null);  // survives inside the page session
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [latestBranding, setLatestBranding] = useState({ coverFileRef:'', coverFileName:'', logoFileRef:'', logoFileName:'' });
  const [sourceWizardOpen, setSourceWizardOpen] = useState(false);
  const [sourceWizardFilter, setSourceWizardFilter] = useState('');
  const [applyingSources, setApplyingSources] = useState(null); // { total, done, current }
  const [narrativeStatus, setNarrativeStatus] = useState({}); // { sectionId: 'AI' | 'DET' }
  const [sectionOrder, setSectionOrder] = useState([]); // Array<sectionId> — user-defined drag order, persisted per preset
  const [sectionPhotos, setSectionPhotos] = useState({}); // { [sectionId]: [{photoId, caption, originalName, url}] }
  // Photo lightbox / carousel — one-click preview of a section's photos.
  const [lightbox, setLightbox] = useState(null);         // { sectionId, index }
  const openLightbox = (sectionId, index) => setLightbox({ sectionId, index });
  const closeLightbox = () => setLightbox(null);
  const stepLightbox = (dir) => {
    if (!lightbox) return;
    const list = sectionPhotos[lightbox.sectionId] || [];
    if (!list.length) return;
    const next = (lightbox.index + dir + list.length) % list.length;
    setLightbox({ ...lightbox, index: next });
  };

  // Restore any prior "generate-html" run persisted before a tab switch /
  // refresh so users don't lose their download links or the progress-bar.
  useEffect(() => {
    if (!projectId || !reportId) return;
    try {
      const raw = localStorage.getItem(`report-gen:${projectId}:${reportId}`);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (state.status === 'PROCESSING' && (Date.now() - (state.startedAt || 0)) < 20 * 60 * 1000) {
        // Job is likely still running on the server — resume polling.
        setGenJob({ job_id: state.jobId || null, status: 'PROCESSING', percent: 15, step: 'Reconnecting to Cognara run…' });
        (async () => {
          for (let i = 0; i < 240; i++) {
            await new Promise(res => setTimeout(res, 2000));
            try {
              const s = state.jobId
                ? await api.get(`/v1/projects/${projectId}/reports/${reportId}/narrate/${state.jobId}`)
                : null;
              if (s?.data) setGenJob(s.data);
              if (s?.data?.status === 'COMPLETED' || s?.data?.status === 'FAILED') break;
            } catch {}
          }
          const url = `${API}/api/v1/projects/${projectId}/reports/${reportId}/download-html`;
          setGeneratedHtml(url);
          localStorage.setItem(`report-gen:${projectId}:${reportId}`, JSON.stringify({ finishedAt: Date.now(), status: 'COMPLETED', url, jobId: state.jobId }));
          setGenJob(j => ({ ...(j || {}), status: 'COMPLETED', percent: 100 }));
        })();
      } else if (state.status === 'COMPLETED' && state.url) {
        setGeneratedHtml(state.url);
        setGenJob({ status: 'COMPLETED', percent: 100, step: 'Restored from previous run' });
      }
    } catch { /* ignore */ }
  }, [projectId, reportId]);

  // Refresh sectionPhotos map from the persisted report doc.
  const refreshSectionPhotos = async (rid) => {
    if (!rid) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/reports/${rid}`);
      const map = {};
      (data?.sections || []).forEach(sec => {
        const ph = (sec.manualContent || {}).photos || [];
        if (ph.length) map[sec.sectionId] = ph;
      });
      setSectionPhotos(map);
    } catch { /* ignore */ }
  };

  // Client-side image resize — most site photos are 5-12 MB straight off a
  // phone which trips the Cloudflare 100 MB per-request ceiling AND the
  // 60 s ingress timeout when the network is slow. Down-sampling to
  // ≤ 1920 px longest edge with JPEG quality 0.85 keeps images visually
  // pristine while dropping them to 300-800 KB, so uploads succeed even on
  // 4G and the slide preview / PDF render instantly.
  const _resizeImage = async (file, maxDim = 1920, quality = 0.85) => {
    if (!file.type?.startsWith('image/')) return file;                       // skip non-images
    if (file.size < 800 * 1024) return file;                                 // already small enough
    try {
      const bmp = await createImageBitmap(file);
      const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
      if (scale >= 1) { bmp.close?.(); return file; }
      const w = Math.round(bmp.width * scale);
      const h = Math.round(bmp.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
      bmp.close?.();
      const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', quality));
      if (!blob) return file;
      // Preserve original name so the caption prompt reads sensibly.
      return new File([blob], file.name.replace(/\.(png|webp|heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
    } catch { return file; }
  };

  const uploadSectionPhotos = async (sectionId, files) => {
    if (!files?.length) return;
    let rid = reportId;
    if (!rid) rid = await createOrGetReport();
    if (!rid) { toast.error('Save the report meta first — no report_id'); return; }
    const toastId = toast.loading(`Uploading ${files.length} photo${files.length > 1 ? 's' : ''}…`);
    let ok = 0, fail = 0;
    try {
      for (const raw of files) {
        try {
          const file = await _resizeImage(raw);
          const fd = new FormData();
          fd.append('file', file);
          fd.append('caption', '');
          // 60 s timeout — Cloudflare's default is 100 s so we surface a
          // clearer message before that hits.
          await api.post(
            `/v1/projects/${projectId}/reports/${rid}/sections/${sectionId}/photos`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 }
          );
          ok += 1;
        } catch (e) {
          fail += 1;
          const detail = e?.response?.data?.detail || e?.response?.statusText || e?.message || 'upload failed';
          toast.error(`"${raw.name}": ${detail}`);
        }
      }
      await refreshSectionPhotos(rid);
      if (previewOpen[sectionId]) previewSection(sectionId, true);
      if (ok && !fail) toast.success(`Uploaded ${ok} photo${ok > 1 ? 's' : ''}`, { id: toastId });
      else if (ok && fail) toast.warning(`Uploaded ${ok}, ${fail} failed`, { id: toastId });
      else toast.error('All photo uploads failed — see errors above', { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Photo upload crashed', { id: toastId });
    }
  };

  const deleteSectionPhoto = async (sectionId, photoId) => {
    if (!reportId) return;
    try {
      await api.delete(`/v1/projects/${projectId}/reports/${reportId}/sections/${sectionId}/photos/${photoId}`);
      await refreshSectionPhotos(reportId);
      if (previewOpen[sectionId]) previewSection(sectionId, true);
      toast.success('Photo deleted');
    } catch { toast.error('Delete failed'); }
  };

  const updatePhotoCaption = async (sectionId, photoId, caption) => {
    if (!reportId) return;
    try {
      await api.patch(
        `/v1/projects/${projectId}/reports/${reportId}/sections/${sectionId}/photos/${photoId}`,
        { caption }
      );
      await refreshSectionPhotos(reportId);
      if (previewOpen[sectionId]) previewSection(sectionId, true);
    } catch { /* backend endpoint may not exist yet — silent no-op */ }
  };

  // Drag-drop reorder within a section — moves `srcId` before `targetId`.
  // Persists via `POST .../photos/reorder` with the new [photoId] order.
  const reorderSectionPhoto = async (sectionId, srcId, targetId) => {
    const list = [...(sectionPhotos[sectionId] || [])];
    const srcIdx = list.findIndex(p => p.photoId === srcId);
    const tgtIdx = list.findIndex(p => p.photoId === targetId);
    if (srcIdx < 0 || tgtIdx < 0 || srcIdx === tgtIdx) return;
    const [moved] = list.splice(srcIdx, 1);
    list.splice(tgtIdx, 0, moved);
    setSectionPhotos(prev => ({ ...prev, [sectionId]: list })); // optimistic
    const order = list.map(p => p.photoId);
    try {
      await api.post(
        `/v1/projects/${projectId}/reports/${reportId}/sections/${sectionId}/photos/reorder`,
        { order }
      );
      if (previewOpen[sectionId]) previewSection(sectionId, true);
      toast.success('Photo order saved');
    } catch (e) {
      // Rollback on failure
      await refreshSectionPhotos(reportId);
      toast.error('Reorder failed');
    }
  };

  // ── Platform module tree (mirrors the left-nav + Project Controls tabs).
  // Each leaf's `v` maps to a `sourceModule` value the backend collector
  // dispatcher recognises. Users see the SAME names they navigate to.
  const PLATFORM_MODULES = [
    { root:'Portfolio', color:'#0EA5E9', Icon: LayoutGrid, subs:[
      { v:'MULTI',        label:'Portfolio Analytics · Waterfall' },
      { v:'PROJECT_MGMT', label:'Portfolio · Projects Snapshot' },
    ]},
    { root:'Projects', color:'#0EA5E9', Icon: Briefcase, subs:[
      { v:'PROJECT',      label:'Project Overview / KPIs' },
    ]},
    { root:'Project Controls · Schedule Management', color:'#3B82F6', Icon: Calendar, subs:[
      { v:'MACRO_SCHEDULE',   label:'Macro Schedule (Master)' },
      { v:'DCI',              label:'Engineering Schedule (DCI)' },
      { v:'MCI',              label:'Procurement Schedule (MCI)' },
      { v:'FC_ACTIVITIES',    label:'Fabrication & Construction' },
      { v:'PRECOMM',          label:'Pre-Comm & Commissioning' },
      { v:'4D_MODEL',         label:'4D Modelling' },
      { v:'SCHEDULE_ROLLUP',  label:'Schedule Roll-Up' },
    ]},
    { root:'Project Controls · Cost Management', color:'#F97316', Icon: DollarSign, subs:[
      { v:'COST_CBS',                    label:'CBS Matrix' },
      { v:'COST_MGMT|invoice',           label:'Invoice Management' },
      { v:'COST_MGMT|working_capital',   label:'Working Capital (Cashflow)' },
      { v:'COST_MGMT|evm',               label:'Earned Value Management' },
      { v:'COST_CBS|budget_revision',    label:'Budget Revision Tracker' },
      { v:'COST_CBS|commitment',         label:'Commitment Tracking' },
      { v:'RESOURCES',                   label:'Manpower Management' },
      { v:'EQUIPMENT',                   label:'Equipment Management' },
    ]},
    { root:'HSE Management',     color:'#10B981', Icon: ShieldAlert, subs:[{ v:'HSE',  label:'HSE · Statistics & Incidents' }]},
    { root:'RAID Log',           color:'#EF4444', Icon: AlertTriangle, subs:[{ v:'RAID', label:'Risks / Issues / Assumptions / Dependencies' }]},
    { root:'Site Monitoring',    color:'#10B981', Icon: Camera, subs:[{ v:'SITE_MONITORING', label:'Cameras / Photos / Safety Alerts' }]},
    { root:'Piping Workfront',   color:'#0EA5E9', Icon: Wrench, subs:[{ v:'PIPING_WORKFRONT', label:'Spools · BoQ · Progress' }]},
    { root:'Structural Workfront', color:'#0EA5E9', Icon: Ruler, subs:[{ v:'STRUCTURAL_WORKFRONT', label:'Mark Numbers · Erection' }]},
    { root:'Material Tracking',  color:'#F97316', Icon: Package, subs:[{ v:'MATERIAL', label:'MTM Register · Stock · Long-Lead' }]},
    { root:'Contracts & Claims', color:'#8B5CF6', Icon: FileSignature, subs:[
      { v:'CLM',              label:'Claims · LDs · Variations' },
      { v:'COMMERCIAL|pay',   label:'Commercial · Payments' },
    ]},
    { root:'Scan-to-Model',      color:'#10B981', Icon: ScanLine, subs:[{ v:'SCAN3D', label:'Scan vs Design · Compare' }]},
    { root:'Tenders',            color:'#8B5CF6', Icon: Handshake, subs:[{ v:'TENDER', label:'BD Pipeline / Bids' }]},
    { root:'Document Management',color:'#0EA5E9', Icon: FolderKanban, subs:[{ v:'DMS', label:'EDMS · Documents · Reviews' }]},
    { root:'AI Analyst',         color:'#6B5CE7', Icon: Bot, subs:[{ v:'NARRATIVE|ai',  label:'Cognara · AI Narrative' }]},
    { root:'CEO Briefing',       color:'#6B5CE7', Icon: PenTool, subs:[{ v:'NARRATIVE|ceo', label:'Cognara · Board Brief' }]},
    { root:'Manual (editor)',    color:'#64748B', Icon: Edit3, subs:[{ v:'MANUAL', label:'Author content in-line' }]},
  ];

  // Which top-level roots are relevant for each section. First entry = default.
  const ALLOWED_ROOTS = {
    project_snapshot:      ['Projects', 'Portfolio'],
    portfolio:             ['Portfolio', 'Projects'],
    portfolio_waterfall:   ['Portfolio', 'Project Controls · Cost Management'],
    macro_schedule:        ['Project Controls · Schedule Management'],
    schedule_rollup:       ['Project Controls · Schedule Management'],
    engineering_dci:       ['Project Controls · Schedule Management'],
    procurement_mci:       ['Project Controls · Schedule Management'],
    fabrication_construction:['Project Controls · Schedule Management', 'Piping Workfront', 'Structural Workfront'],
    pre_commissioning:     ['Project Controls · Schedule Management'],
    cost_control:          ['Project Controls · Cost Management'],
    evm:                   ['Project Controls · Cost Management'],
    working_capital:       ['Project Controls · Cost Management'],
    cost_cbs:              ['Project Controls · Cost Management'],
    cbs_matrix:            ['Project Controls · Cost Management'],
    budget_revision:       ['Project Controls · Cost Management'],
    commitment_tracking:   ['Project Controls · Cost Management'],
    invoice_mgmt:          ['Project Controls · Cost Management', 'Contracts & Claims'],
    manpower_mgmt:         ['Project Controls · Cost Management'],
    equipment_mgmt:        ['Project Controls · Cost Management'],
    material_tracking:     ['Material Tracking', 'Piping Workfront'],
    document_mgmt:         ['Document Management'],
    tenders:               ['Tenders', 'Contracts & Claims'],
    hse:                   ['HSE Management'],
    raid:                  ['RAID Log'],
    piping_workfront:      ['Piping Workfront', 'Project Controls · Schedule Management'],
    structural_workfront:  ['Structural Workfront', 'Project Controls · Schedule Management'],
    clm:                   ['Contracts & Claims'],
    fourd:                 ['Project Controls · Schedule Management'],
    vpr:                   ['Site Monitoring', 'Project Controls · Schedule Management'],
    scan3d:                ['Scan-to-Model'],
    site_monitoring:       ['Site Monitoring', 'HSE Management'],
    exec_summary:          ['AI Analyst', 'RAID Log', 'Project Controls · Schedule Management'],
    what_changed:          ['AI Analyst', 'Project Controls · Schedule Management', 'Project Controls · Cost Management'],
    watchlist:             ['AI Analyst', 'RAID Log'],
    ask_of_client:         ['AI Analyst', 'RAID Log', 'Contracts & Claims'],
    look_ahead:            ['AI Analyst', 'Project Controls · Schedule Management'],
    root_cause_callouts:   ['AI Analyst', 'RAID Log'],
    ai_analyst:            ['AI Analyst', 'RAID Log'],
    ceo_briefing:          ['CEO Briefing', 'Projects'],
    photo_strip:           ['Site Monitoring', 'Manual (editor)'],
    manual_narrative:      ['Manual (editor)'],
  };

  const allowedRootsFor = (sid) => {
    // Preferred roots first (from ALLOWED_ROOTS), then EVERY other root on the
    // platform so advanced users can source a section from any module.
    const pref = ALLOWED_ROOTS[sid] || [];
    const all  = PLATFORM_MODULES.map(m => m.root);
    const rest = all.filter(r => !pref.includes(r));
    return [...pref, ...rest];
  };
  const findRootFor = (v) => PLATFORM_MODULES.find(m => m.subs.some(s => s.v === v));
  const currentRootLabelFor = (sid, sel, s) => {
    const v = (sel?.sources && sel.sources[0]) || sel?.sourceModule || s?.module;
    const r = findRootFor(v);
    return r?.root || allowedRootsFor(sid)[0] || '';
  };
  const currentSubLabelFor = (sel, s) => {
    const arr = sel?.sources || (sel?.sourceModule ? [sel.sourceModule] : [s?.module]);
    const labels = arr.map(v => {
      const r = findRootFor(v);
      return r?.subs.find(x => x.v === v)?.label || v;
    });
    return labels.length > 1 ? `${labels[0]} +${labels.length - 1} more` : labels[0];
  };

  // Multi-source setter (array). Empty array → falls back to section default.
  const setSourceMulti = (id, arr) => setSelected(p => ({ ...p, [id]: { ...(p[id] || {}), sources: arr, sourceModule: arr[0] || undefined } }));

  // Legacy shim so per-row "change source" link still works
  const alternativesFor = (sid) => (allowedRootsFor(sid).length ? [] : null);

  // ── Hydrate wizard state from the latest persisted report so Author All /
  // Refresh Data status survives navigation. Also seeds the "N/N Authored"
  // chip and the "Loaded: <presetName>" pill on cold page load.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await api.get(`/v1/projects/${projectId}/reports`);
        const rows = Array.isArray(list.data) ? list.data : [];
        if (!rows.length || cancelled) return;
        // Take the most recently updated report
        rows.sort((a, b) => (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || ''));
        const latest = rows[0];
        const rid = latest?._id || latest?.id;
        if (!rid) return;
        const r = await api.get(`/v1/projects/${projectId}/reports/${rid}`);
        if (cancelled) return;
        const doc = r.data || {};
        setReportId(rid);
        // Narratives → authored chip
        const narr = doc.aiNarratives || {};
        const status = {};
        Object.entries(narr).forEach(([sid, n]) => {
          if (n && typeof n === 'object' && (n.insight || n.headline || n.narrative || (n.recommendations && n.recommendations.length))) {
            status[sid] = 'AI';
          }
        });
        setNarrativeStatus(status);
        if (doc.aiNarrativesGeneratedAt) setLastAuthoredAt(doc.aiNarrativesGeneratedAt);
        if (doc.dataHealthRefreshedAt)   setLastRefreshedAt(doc.dataHealthRefreshedAt);
        if (doc.activePresetName)        setActivePresetName(doc.activePresetName);
      } catch { /* first-time projects have no reports yet */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const fetchHealth = () => api.get(`/v1/projects/${projectId}/reports/data-health`)
      .then(r => { if (!cancelled) setDataHealth(r.data?.sections || {}); })
      .catch(() => { if (!cancelled) setDataHealth({}); });
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/v1/reports/section-catalog?report_type=${type}`);
        const secs = r.data?.sections || [];
        setCatalog(secs);
        setSelected(prev => {
          const next = { ...prev };
          secs.forEach(s => {
            if (!next[s.id]) next[s.id] = {
              enabled: s.premium || s.id === 'cover' || s.id === 'project_snapshot',
              aiEnabled: !!s.requiresAI,
              subModules: Object.fromEntries((s.subModules || []).map(sm => [sm, true])),
            };
          });
          return next;
        });
      } catch { toast.error('Failed to load section catalog'); }
    })();
  }, [type]);

  const [previewOpen, setPreviewOpen] = useState({});
  const [previewHtml, setPreviewHtml] = useState({});
  const [iterating, setIterating] = useState({});
  const [iterateInput, setIterateInput] = useState({});
  const [brandTheme, setBrandTheme] = useState({ accentColor: '#6B5CE7', headerColor: '#0D1B2A' });

  useEffect(() => {
    if (!projectId) return;
    api.get(`/v1/reports/projects/${projectId}/brand-theme`).then(r => setBrandTheme(r.data || {})).catch(() => {});
  }, [projectId]);

  const saveBrandTheme = async () => {
    try { await api.put(`/v1/reports/projects/${projectId}/brand-theme`, brandTheme); toast.success('Brand theme saved'); }
    catch { toast.error('Save failed'); }
  };

  const previewSection = async (sectionId, force = false) => {
    const id = await createOrGetReport();
    if (!id) return;
    // Toggle open only when NOT force-refreshing; force keeps it open + re-fetches.
    if (!force) setPreviewOpen(o => ({ ...o, [sectionId]: !o[sectionId] }));
    if (!force && previewHtml[sectionId]) return;
    try {
      const url = `/v1/projects/${projectId}/reports/${id}/sections/${sectionId}/preview${force ? `?ts=${Date.now()}` : ''}`;
      const r = await api.get(url);
      setPreviewHtml(h => ({ ...h, [sectionId]: r.data?.html || '<p>No preview</p>' }));
      if (force) { setPreviewOpen(o => ({ ...o, [sectionId]: true })); toast.success('Slide refreshed'); }
    } catch { toast.error('Preview failed'); }
  };

  // Iframe click-through: heatmap cells + discipline s-curves postMessage
  // { kind: 'cognara_prefill', sectionId, prompt } — wizard prefills the
  // iterate box so the user hits Enter to get an AI root-cause.
  useEffect(() => {
    const handler = (e) => {
      const m = e.data || {};
      if (m.kind !== 'cognara_prefill' || !m.sectionId) return;
      setIterateInput(x => ({ ...x, [m.sectionId]: m.prompt || '' }));
      // scroll section into view + focus
      const el = document.querySelector(`[data-testid=sec-iterate-input-${m.sectionId}]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      toast.info(`Prefilled Cognara prompt for ${m.sectionId}`);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const iterateSection = async (sectionId) => {
    const id = await createOrGetReport();
    if (!id) return;
    setIterating(x => ({ ...x, [sectionId]: true }));
    try {
      await api.post(`/v1/projects/${projectId}/reports/${id}/sections/${sectionId}/iterate`, { instruction: iterateInput[sectionId] || '' });
      toast.success('Cognara refined the section');
      // Re-fetch preview
      const r = await api.get(`/v1/projects/${projectId}/reports/${id}/sections/${sectionId}/preview`);
      setPreviewHtml(h => ({ ...h, [sectionId]: r.data?.html || '' }));
      setPreviewOpen(o => ({ ...o, [sectionId]: true }));
    } catch { toast.error('Iterate failed'); }
    finally { setIterating(x => ({ ...x, [sectionId]: false })); }
  };

const MODULE_COLORS = {
  META: '#64748B', NARRATIVE: '#6B5CE7', PROJECT: '#0EA5E9', PROJECT_MGMT: '#0EA5E9',
  MACRO_SCHEDULE: '#3B82F6', SCHEDULE_MGMT: '#3B82F6', SCHEDULE_ROLLUP: '#3B82F6',
  DCI: '#3B82F6', MCI: '#3B82F6', '4D_MODEL': '#3B82F6',
  COST_CONTROL: '#F97316', COST_MGMT: '#F97316', COST_CBS: '#F97316',
  QPT: '#10B981', HSE: '#10B981', RAID: '#10B981', GOVERNANCE: '#10B981',
  SITE_MONITORING: '#10B981', PIPING_WORKFRONT: '#10B981', STRUCTURAL_WORKFRONT: '#10B981',
  VPR: '#10B981', SCAN3D: '#10B981',
  CLM: '#8B5CF6', COMMERCIAL: '#8B5CF6',
  MANUAL: '#64748B',
};

  // ── Ordered catalog: honour user-defined `sectionOrder` for enabled
  // sections, fallback to the catalog's natural order for unranked / disabled
  // sections.  Every downstream consumer (module grouping, slide strip,
  // report sections payload) reads from this list so drag-drop persists.
  const orderedCatalog = useMemo(() => {
    if (!catalog.length) return [];
    const byId = Object.fromEntries(catalog.map(s => [s.id, s]));
    const ordered = [];
    const seen = new Set();
    (sectionOrder || []).forEach(id => {
      if (byId[id] && !seen.has(id)) { ordered.push(byId[id]); seen.add(id); }
    });
    catalog.forEach(s => { if (!seen.has(s.id)) { ordered.push(s); seen.add(s.id); } });
    return ordered;
  }, [catalog, sectionOrder]);

  const moveSectionOrder = (srcId, dstId) => {
    if (!srcId || !dstId || srcId === dstId) return;
    setSectionOrder(prev => {
      const currentIds = orderedCatalog.map(s => s.id);
      const list = (prev && prev.length ? prev.filter(id => currentIds.includes(id)) : currentIds.slice());
      // Ensure both ids are present (append if missing so the reorder can happen)
      if (!list.includes(srcId)) list.push(srcId);
      if (!list.includes(dstId)) list.push(dstId);
      const srcIdx = list.indexOf(srcId);
      const dstIdx = list.indexOf(dstId);
      list.splice(srcIdx, 1);
      const newDst = list.indexOf(dstId);
      list.splice(newDst, 0, srcId);
      return list;
    });
  };

  const grouped = useMemo(() => {
    const g = {};
    orderedCatalog.forEach(s => { (g[s.module] = g[s.module] || []).push(s); });
    return g;
  }, [orderedCatalog]);

  const toggleSection = (id, on) => setSelected(p => ({ ...p, [id]: { ...(p[id] || {}), enabled: on } }));
  const toggleAI = (id, on) => setSelected(p => ({ ...p, [id]: { ...(p[id] || {}), aiEnabled: on } }));
  const toggleSub = (id, sub, on) => setSelected(p => ({ ...p, [id]: { ...(p[id] || {}), subModules: { ...(p[id]?.subModules || {}), [sub]: on } } }));
  const setSourceOverride = (id, v) => setSelected(p => ({ ...p, [id]: { ...(p[id] || {}), sourceModule: v } }));

  // ── Wizard Preset persistence ────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try { const r = await api.get(`/v1/projects/${projectId}/reports/wizard-preset`); setPresets(r.data || []); } catch {}
    })();
  }, [projectId]);

  const savePreset = async () => {
    const name = (presetName || '').trim() || `Preset · ${new Date().toISOString().slice(0,10)}`;
    try {
      await api.post(`/v1/projects/${projectId}/reports/wizard-preset`, {
        name,
        reportType: type,
        selected,
        sectionOrder,
        code: meta.code,
        accentColor: brandTheme?.accentColor || '#6B5CE7',
        brandTheme,
        meta,
        // Attach the most recently uploaded cover/logo so next load can
        // auto-fetch them from the branding endpoint.
        coverFileRef:  latestBranding?.coverFileRef  || '',
        coverFileName: latestBranding?.coverFileName || (coverFile?.name || ''),
        logoFileRef:   latestBranding?.logoFileRef   || '',
        logoFileName:  latestBranding?.logoFileName  || (logoFile?.name || ''),
      });
      const r = await api.get(`/v1/projects/${projectId}/reports/wizard-preset`);
      setPresets(r.data || []);
      // Reflect the just-saved preset as the currently-loaded one so the
      // "Loaded: <presetName>" pill shows without needing a reload, AND
      // stamp it on the latest report so a cold reload still shows it.
      setActivePresetName(name);
      if (reportId) {
        try { await api.put(`/v1/projects/${projectId}/reports/${reportId}`, { activePresetName: name }); } catch {}
      }
      toast.success(`Preset "${name}" saved with brand & cover refs`);
    } catch { toast.error('Save preset failed'); }
  };

  const loadPreset = (p) => {
    if (!p) return;
    setSelected(p.selected || {});
    setSectionOrder(Array.isArray(p.sectionOrder) ? p.sectionOrder : []);
    setActivePresetName(p.name || '');
    if (p.reportType) setType(p.reportType);
    if (p.brandTheme) setBrandTheme(prev => ({ ...prev, ...p.brandTheme }));
    if (p.meta) setMeta(prev => ({ ...prev, ...p.meta }));
    // Restore cover / logo previews so the user sees what will be applied
    setLatestBranding({
      coverFileRef:  p.coverFileRef  || '',
      coverFileName: p.coverFileName || '',
      logoFileRef:   p.logoFileRef   || '',
      logoFileName:  p.logoFileName  || '',
    });
    toast.success(`Loaded preset "${p.name}" — cover & logo re-linked`);
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/v1/projects/${projectId}/reports/wizard-preset/${id}`);
      const r = await api.get(`/v1/projects/${projectId}/reports/wizard-preset`);
      setPresets(r.data || []);
      toast.success('Preset deleted');
    } catch { toast.error('Delete failed'); }
  };

  const createOrGetReport = async () => {
    if (reportId) return reportId;
    const sections = orderedCatalog
      .filter(s => selected[s.id]?.enabled)
      .map((s, i) => ({
        sectionId: s.id, order: i, hidden: false,
        // Compound `MODULE|variant` values are unique to the UI dropdown but
        // strip the variant suffix before sending to the backend collector
        // so it still resolves to a known `sourceModule` key.
        sourceModule: (selected[s.id]?.sourceModule || (selected[s.id]?.sources || [])[0] || s.module).split('|')[0],
        sourceVariant: ((selected[s.id]?.sourceModule || (selected[s.id]?.sources || [])[0] || '').split('|')[1]) || null,
        sources: (selected[s.id]?.sources || []).map(v => v.split('|')[0]),
        layout: s.layout,
        subModules: Object.entries(selected[s.id]?.subModules || {}).filter(([, v]) => v).map(([k]) => k),
        requiresAI: !!selected[s.id]?.aiEnabled,
      }));
    const payload = {
      reportType: type, title: meta.title || `${type} — ${projectName || ''}`,
      client: meta.client, author: meta.author,
      projectName, projectId,
      period: { startDate: meta.startDate, endDate: meta.endDate },
      sections,
    };
    try {
      const r = await api.post(`/v1/projects/${projectId}/reports`, payload);
      const id = r.data?._id || r.data?.id;
      setReportId(id);
      return id;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create report failed'); return null; }
  };

  const uploadCoverAndLogo = async (id) => {
    if (coverFile) {
      const fd = new FormData(); fd.append('kind', 'cover'); fd.append('file', coverFile);
      try { await api.post(`/v1/projects/${projectId}/reports/${id}/branding`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
    }
    if (logoFile) {
      const fd = new FormData(); fd.append('kind', 'logo'); fd.append('file', logoFile);
      try { await api.post(`/v1/projects/${projectId}/reports/${id}/branding`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
    }
  };

  const generateHTML = async () => {
    const id = await createOrGetReport();
    if (!id) return;
    await uploadCoverAndLogo(id);
    setGenJob({ job_id: null, status: 'QUEUED', percent: 5, step: 'Starting…' });
    // Persist an in-flight marker so a page refresh / tab switch mid-run
    // still surfaces the running state and hydrates the download links
    // once the server finishes.
    const _key = `report-gen:${projectId}:${id}`;
    localStorage.setItem(_key, JSON.stringify({ startedAt: Date.now(), status: 'PROCESSING' }));
    try {
      const r = await api.post(`/v1/projects/${projectId}/reports/${id}/generate-narrated-html`);
      const jobId = r.data?.job_id;
      setGenJob({ job_id: jobId, status: 'PROCESSING', percent: 10, step: 'Cognara narrating…' });
      localStorage.setItem(_key, JSON.stringify({ startedAt: Date.now(), jobId, status: 'PROCESSING' }));

      // Poll narration status; then poll html readiness
      if (jobId) {
        for (let i = 0; i < 240; i++) {
          await new Promise(res => setTimeout(res, 2000));
          try {
            const s = await api.get(`/v1/projects/${projectId}/reports/${id}/narrate/${jobId}`);
            setGenJob(s.data);
            if (s.data?.status === 'COMPLETED' || s.data?.status === 'FAILED') break;
          } catch {}
        }
      }
      // Small buffer for the render task to write the file
      await new Promise(res => setTimeout(res, 3000));
      const url = `${API}/api/v1/projects/${projectId}/reports/${id}/download-html`;
      setGeneratedHtml(url);
      localStorage.setItem(_key, JSON.stringify({ finishedAt: Date.now(), status: 'COMPLETED', url, jobId }));
      toast.success('Report ready — open, download HTML or export PDF');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'HTML generation failed');
      localStorage.setItem(_key, JSON.stringify({ finishedAt: Date.now(), status: 'FAILED' }));
    } finally {
      setGenJob(j => ({ ...(j || {}), status: j?.status === 'FAILED' ? 'FAILED' : 'COMPLETED', percent: 100 }));
    }
  };

  const generatePDF = async () => {
    const id = await createOrGetReport();
    if (!id) return;
    await uploadCoverAndLogo(id);
    setGenJob({ job_id: null, status: 'QUEUED', percent: 5, step: 'Rendering PDF via WeasyPrint…' });
    const _key = `report-gen-pdf:${projectId}:${id}`;
    localStorage.setItem(_key, JSON.stringify({ startedAt: Date.now(), status: 'PROCESSING' }));
    try {
      // The server-side PDF endpoint streams synchronously — trigger a
      // download via a hidden <a> so the browser handles the file save.
      const pdfUrl = `${API}/api/v1/projects/${projectId}/reports/${id}/download-pdf-v2`;
      const a = document.createElement('a');
      a.href = pdfUrl; a.download = ''; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      // Also stash the URL so the "Download PDF" button in the progress
      // card links to the same file after the run.
      setGeneratedHtml(prev => prev || pdfUrl.replace('/download-pdf-v2', '/download-html'));
      localStorage.setItem(_key, JSON.stringify({ finishedAt: Date.now(), status: 'COMPLETED', url: pdfUrl }));
      toast.success('PDF export started — the browser will save it in a moment');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF export failed');
      localStorage.setItem(_key, JSON.stringify({ finishedAt: Date.now(), status: 'FAILED' }));
    } finally {
      setGenJob(j => ({ ...(j || {}), status: j?.status === 'FAILED' ? 'FAILED' : 'COMPLETED', percent: 100 }));
    }
  };

  const generatePPTX = async () => {
    const id = await createOrGetReport();
    if (!id) return;
    await uploadCoverAndLogo(id);
    try {
      await api.post(`/v1/projects/${projectId}/reports/${id}/generate`);
      toast.success('PPTX generation queued — download from Report Runs tab shortly');
    } catch (e) { toast.error('PPTX generation failed'); }
  };

  const generateDOCX = async () => {
    const id = await createOrGetReport();
    if (!id) return;
    await uploadCoverAndLogo(id);
    setDocxBusy(true);
    try {
      toast.info('Building the Word document — branded cover, TOC, charts…');
      await api.post(`/v1/projects/${projectId}/reports/${id}/generate-docx`);
      const url = `${API}/api/v1/projects/${projectId}/reports/${id}/download-docx`;
      const a = document.createElement('a');
      a.href = url; a.download = ''; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setGeneratedDocx(url);
      toast.success('Word document ready — downloading now');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Word export failed');
    } finally {
      setDocxBusy(false);
    }
  };

  // ── Author All: resumable ──────────────────────────────────────────
  // The narration job lives on the server (`report.generation.activeNarrationJob`
  // + the `reportNarrativeJobs` collection), so authoring keeps running when the
  // tab changes, the page is refreshed or the backend restarts. This poller just
  // re-attaches to whatever job is in flight.
  const pollingRef = useRef(null);

  const attachToJob = useCallback(async (reportIdArg, jobIdArg, totalHint) => {
    if (pollingRef.current) return;                 // already attached
    pollingRef.current = jobIdArg || 'probe';
    try {
      for (let i = 0; i < 400; i++) {
        let st = null;
        try {
          st = jobIdArg
            ? (await api.get(`/v1/projects/${projectId}/reports/${reportIdArg}/narrate/${jobIdArg}`)).data
            : (await api.get(`/v1/projects/${projectId}/reports/${reportIdArg}/active-job`)).data;
        } catch { /* transient */ }
        if (st) {
          const done = st.results ? Object.keys(st.results).length : (st.done || 0);
          const total = st.section_count || st.total || totalHint || 1;
          const label = st.current_section || st.step || 'Authoring…';
          setApplyingSources({ total, done: Math.min(done, total), current: label, mode: 'author' });
          if (st.status === 'COMPLETED' || st.status === 'FAILED') {
            setApplyingSources(null);
            try {
              const r = await api.get(`/v1/projects/${projectId}/reports/${reportIdArg}`);
              const narr = r.data?.aiNarratives || {};
              const status = {};
              Object.entries(narr).forEach(([sid, n]) => {
                if (n && (n.insight || n.headline || n.narrative || (n.recommendations && n.recommendations.length))) status[sid] = 'AI';
              });
              setNarrativeStatus(status);
              if (r.data?.aiNarrativesGeneratedAt) setLastAuthoredAt(r.data.aiNarrativesGeneratedAt);
            } catch {}
            setPreviewHtml({});
            localStorage.removeItem(`report-author:${projectId}:${reportIdArg}`);
            if (st.status === 'COMPLETED') {
              toast.success(`Cognara authored ${done} section${done === 1 ? '' : 's'}`);
              try {
                if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                  new Notification('Author All complete', { body: `${done} sections authored by Cognara` });
                }
              } catch {}
            } else {
              toast.error(st.error || 'Authoring failed — Cognara AI engine unavailable');
            }
            break;
          }
        }
        await new Promise(res => setTimeout(res, 3000));
      }
    } finally {
      pollingRef.current = null;
    }
  }, [projectId]);

  // Re-attach on mount / when the report id becomes known (covers refresh + tab change)
  useEffect(() => {
    if (!projectId || !reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/reports/${reportId}/active-job`);
        if (cancelled || !data?.active) return;
        setApplyingSources({
          total: data.total || 1, done: data.done || 0,
          current: data.current_section || data.step || 'Resuming authoring…', mode: 'author',
        });
        toast.info('Author All is still running — reattached to the live job');
        attachToJob(reportId, data.job_id, data.total);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [projectId, reportId, attachToJob]);

  const enabledCount = Object.values(selected).filter(x => x?.enabled).length;
  // "Authored" chip reflects sections that actually have a Cognara narrative,
  // NOT the static `requiresAI` flag — so after Author All runs the counter
  // reflects real authorship state (e.g. 25/25 instead of a stuck 3/25).
  const authoredCount = Object.keys(narrativeStatus || {}).filter(k => selected[k]?.enabled).length;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="reports-wizard-v2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Progress Reports</h1>
            <p className="text-sm text-muted-foreground">Consulting-grade builder — Cognara narrative, print-quality HTML, editable PPTX.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {applyingSources && (
              <div className="flex items-center gap-2 mr-2 px-2 py-1 rounded bg-purple-500/10 border border-purple-500/30" data-testid="header-progress">
                <Loader2 className="w-3 h-3 animate-spin text-purple-500" />
                <span className="text-[10px] font-semibold text-purple-500">
                  {applyingSources.mode === 'author' ? 'AUTHORING' : (applyingSources.mode === 'refresh' ? 'REFRESHING' : 'APPLYING')} {applyingSources.done}/{applyingSources.total}
                </span>
                <span className="text-[10px] text-muted-foreground max-w-[220px] truncate">{applyingSources.current}</span>
              </div>
            )}
            <Badge variant="outline" className="text-xs">{enabledCount} sections</Badge>
            <Badge variant="outline" className="text-xs" data-testid="header-authored-chip"><Sparkles className="w-3 h-3 mr-1 text-purple-500" />{authoredCount}/{enabledCount} Authored</Badge>
            {activePresetName && (
              <Badge variant="secondary" className="text-xs" data-testid="header-active-preset">
                Loaded: {activePresetName}
              </Badge>
            )}
            {(lastAuthoredAt || lastRefreshedAt) && !applyingSources && (
              <div className="text-[10px] text-muted-foreground flex items-center gap-2" data-testid="header-last-run">
                {lastAuthoredAt  && <span title={new Date(lastAuthoredAt).toLocaleString()}>Author: {new Date(lastAuthoredAt).toLocaleTimeString()}</span>}
                {lastRefreshedAt && <span title={new Date(lastRefreshedAt).toLocaleString()}>Refresh: {new Date(lastRefreshedAt).toLocaleTimeString()}</span>}
              </div>
            )}
            {presets.length > 0 && (
              <select
                className="h-8 text-xs bg-muted border border-border rounded px-2"
                data-testid="preset-load"
                defaultValue=""
                onChange={e => { const p = presets.find(x => x._id === e.target.value); if (p) loadPreset(p); e.target.value = ''; }}
              >
                <option value="" disabled>Load preset…</option>
                {presets.map(p => (<option key={p._id} value={p._id}>{p.name} · {p.reportType}</option>))}
              </select>
            )}
            <Input
              className="h-8 text-xs w-32"
              placeholder="Preset name"
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              data-testid="preset-name"
            />
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="preset-save" onClick={savePreset}>Save Preset</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              data-testid="export-insights-csv"
              onClick={async () => {
                let id = reportId;
                if (!id) id = await createOrGetReport();
                if (!id) { toast.error('Create a report first'); return; }
                const API = process.env.REACT_APP_BACKEND_URL || '';
                window.open(`${API}/api/v1/projects/${projectId}/reports/${id}/insights-export.csv`, '_blank');
                toast.success('Downloading Cognara insight log (CSV)…');
              }}
            ><Download className="w-3 h-3 mr-1" />Insights CSV</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              data-testid="cognara-author-all"
              onClick={async () => {
                const id = await createOrGetReport();
                if (!id) return;
                const modules = catalog.filter(s => selected[s.id]?.enabled).map(s => ({ sid: s.id, label: s.label }));
                if (!modules.length) { toast.info('Enable at least one section first'); return; }
                const total = modules.length;
                setApplyingSources({ total, done: 0, current: 'Kicking off Cognara narration…', mode: 'author' });
                try {
                  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                    Notification.requestPermission().catch(() => {});
                  }
                } catch {}
                try {
                  const kick = await api.post(`/v1/projects/${projectId}/reports/${id}/generate-narrated-html`);
                  const jobId = kick.data?.job_id || null;
                  if (!jobId) throw new Error('No job_id returned — narration cannot be tracked');
                  // Server owns the job — authoring survives tab changes, refreshes
                  // and backend restarts; we just attach a poller to it.
                  localStorage.setItem(`report-author:${projectId}:${id}`, JSON.stringify({ jobId, total, startedAt: Date.now() }));
                  toast.success('Authoring started — you can switch tabs or refresh, it keeps running');
                  attachToJob(id, jobId, total);
                } catch (e) {
                  setApplyingSources(null);
                  toast.error(e?.message?.includes('job_id') ? 'Author All failed — narration did not start (Cognara AI engine unavailable)' : 'Author All failed — Cognara AI engine unavailable');
                }
              }}
            ><Sparkles className="w-3 h-3 mr-1 text-purple-500" />Author All</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              data-testid="open-source-wizard"
              onClick={() => { setSourceWizardFilter(''); setSourceWizardOpen(true); }}
            ><Settings2 className="w-3 h-3 mr-1" />Configure Sources</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              data-testid="refresh-data-health"
              onClick={async () => {
                const modules = catalog.filter(s => selected[s.id]?.enabled).map(s => ({ sid: s.id, label: s.label }));
                if (!modules.length) { toast.info('No sections enabled to refresh'); return; }
                setApplyingSources({ total: modules.length, done: 0, current: 'Refreshing data sources…', mode: 'refresh' });
                try { await api.post(`/v1/projects/${projectId}/reports/data-health/refresh`); } catch {}
                for (let i = 0; i < modules.length; i++) {
                  setApplyingSources({ total: modules.length, done: i, current: modules[i].label, mode: 'refresh' });
                  try {
                    await new Promise(res => setTimeout(res, 220));
                    const h = await api.get(`/v1/projects/${projectId}/reports/data-health?ts=${Date.now()}`);
                    if (i === modules.length - 1) setDataHealth(h.data?.sections || {});
                  } catch {}
                }
                // ── Force-refresh every OPEN slide preview so the user sees updated content
                const openIds = Object.entries(previewOpen).filter(([, v]) => v).map(([k]) => k);
                const id = await createOrGetReport();
                if (id && openIds.length) {
                  for (let j = 0; j < openIds.length; j++) {
                    setApplyingSources({ total: modules.length + openIds.length, done: modules.length + j, current: `Reloading preview: ${openIds[j]}`, mode: 'refresh' });
                    try {
                      const r = await api.get(`/v1/projects/${projectId}/reports/${id}/sections/${openIds[j]}/preview?ts=${Date.now()}`);
                      setPreviewHtml(h => ({ ...h, [openIds[j]]: r.data?.html || h[openIds[j]] }));
                    } catch {}
                  }
                } else {
                  setPreviewHtml({});
                }
                setApplyingSources(null);
                setLastRefreshedAt(new Date().toISOString());
                toast.success(`Refreshed ${modules.length} sections${openIds.length ? ` · ${openIds.length} preview(s) reloaded` : ''}`);
              }}
            ><Loader2 className="w-3 h-3 mr-1 hidden" />Refresh Data</Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Report Basics</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-9 bg-muted border-border" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REPORT_TYPES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Title</Label><Input className="h-9 bg-muted border-border" value={meta.title} onChange={e => setMeta({ ...meta, title: e.target.value })} placeholder="e.g. Week 34 Progress" data-testid="report-title" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Client</Label><Input className="h-9 bg-muted border-border" value={meta.client} onChange={e => setMeta({ ...meta, client: e.target.value })} data-testid="report-client" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Prepared By</Label><Input className="h-9 bg-muted border-border" value={meta.author} onChange={e => setMeta({ ...meta, author: e.target.value })} data-testid="report-author" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Period Start</Label><Input type="date" className="h-9 bg-muted border-border" value={meta.startDate} onChange={e => setMeta({ ...meta, startDate: e.target.value })} data-testid="report-start" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Period End</Label><Input type="date" className="h-9 bg-muted border-border" value={meta.endDate} onChange={e => setMeta({ ...meta, endDate: e.target.value })} data-testid="report-end" /></div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ImageIcon className="w-3 h-3" />Cover Photo</Label>
              <Input type="file" accept="image/*" className="h-9 bg-muted border-border text-xs" onChange={e => setCoverFile(e.target.files?.[0] || null)} data-testid="cover-upload" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ImageIcon className="w-3 h-3" />Client Logo</Label>
              <Input type="file" accept="image/*" className="h-9 bg-muted border-border text-xs" onChange={e => setLogoFile(e.target.files?.[0] || null)} data-testid="logo-upload" />
            </div>
          </CardContent>
        </Card>

        {/* Brand Theme — persists per project so every report reuses the same palette */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Brand Theme (per project)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Accent</Label><Input type="color" className="h-9 w-full bg-muted border-border p-1" value={brandTheme.accentColor || '#6B5CE7'} onChange={e => setBrandTheme({ ...brandTheme, accentColor: e.target.value })} data-testid="brand-accent" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Header</Label><Input type="color" className="h-9 w-full bg-muted border-border p-1" value={brandTheme.headerColor || '#0D1B2A'} onChange={e => setBrandTheme({ ...brandTheme, headerColor: e.target.value })} data-testid="brand-header" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Heading Font</Label><Input className="h-9 bg-muted border-border text-xs" value={brandTheme.fontHeading || 'Inter'} onChange={e => setBrandTheme({ ...brandTheme, fontHeading: e.target.value })} data-testid="brand-font-heading" /></div>
            <div className="space-y-1"><Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Body Font</Label><Input className="h-9 bg-muted border-border text-xs" value={brandTheme.fontBody || 'Inter'} onChange={e => setBrandTheme({ ...brandTheme, fontBody: e.target.value })} data-testid="brand-font-body" /></div>
            <div className="space-y-1 flex items-end"><Button onClick={saveBrandTheme} className="w-full h-9" data-testid="brand-save">Save Brand</Button></div>
          </CardContent>
        </Card>

        {/* Two-level section picker: Module → SubModule */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Sections</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {Object.entries(grouped).map(([module, secs]) => (
              <div key={module} className="border rounded-md overflow-hidden" style={{ borderColor: (MODULE_COLORS[module] || '#3A3F4A') + '55' }}>
                <div className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold" style={{ background: (MODULE_COLORS[module] || '#64748B') + '18', color: MODULE_COLORS[module] || '#64748B', borderBottom: `1px solid ${(MODULE_COLORS[module] || '#3A3F4A')}44` }}>{module.replace(/_/g,' ')}</div>
                {secs.map(s => {
                  const sel = selected[s.id] || {};
                  const isOpen = expanded[s.id];
                  return (
                    <div key={s.id} className="border-t border-border first:border-t-0">
                      <div className="flex items-center gap-2 px-3 py-2">
                                      {sel.enabled && (
                        <button
                          type="button"
                          className="mr-1 text-muted-foreground/60 hover:text-purple-500 cursor-grab active:cursor-grabbing"
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('text/plain', s.id); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const src = e.dataTransfer.getData('text/plain');
                            moveSectionOrder(src, s.id);
                            toast.success('Section reordered — save the preset to persist');
                          }}
                          data-testid={`sec-drag-${s.id}`}
                          title="Drag to reorder — persists when preset is saved"
                        >⋮⋮</button>
                      )}
                      <input type="checkbox" checked={!!sel.enabled} onChange={e => toggleSection(s.id, e.target.checked)} data-testid={`sec-toggle-${s.id}`} />
                        <button className="flex-1 flex items-center gap-2 text-sm text-foreground text-left" onClick={() => setExpanded(x => ({ ...x, [s.id]: !x[s.id] }))} data-testid={`sec-expand-${s.id}`}>
                          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          {s.label}
                          {dataHealth[s.id] && (
                            <span
                              title={`${dataHealth[s.id].collection || 'no source'} · ${dataHealth[s.id].count?.toLocaleString() || 0} rows · ${dataHealth[s.id].days ?? '—'}d old`}
                              data-testid={`sec-health-${s.id}`}
                              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider"
                              style={{
                                background: {FRESH:'#10B98118', WARM:'#F59E0B18', STALE:'#EF444418', EMPTY:'#94A3B818', UNKNOWN:'#94A3B818'}[dataHealth[s.id].band] || '#94A3B818',
                                color:      {FRESH:'#10B981',   WARM:'#F59E0B',   STALE:'#EF4444',   EMPTY:'#64748B',   UNKNOWN:'#64748B'}[dataHealth[s.id].band]   || '#64748B',
                              }}
                            >
                              {dataHealth[s.id].band === 'FRESH' && '● live'}
                              {dataHealth[s.id].band === 'WARM'  && '● warm'}
                              {dataHealth[s.id].band === 'STALE' && '● stale'}
                              {dataHealth[s.id].band === 'EMPTY' && '○ empty'}
                              {dataHealth[s.id].band === 'UNKNOWN' && '○ n/a'}
                            </span>
                          )}
                          {s.premium && <Badge variant="outline" className="text-[9px]">PREMIUM</Badge>}
                          {s.requiresAI && <Badge variant="outline" className="text-[9px] text-purple-500"><Sparkles className="w-2.5 h-2.5 mr-1" />Cognara</Badge>}
                        </button>
                        {s.requiresAI && (
                          <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <input type="checkbox" checked={!!sel.aiEnabled} onChange={e => toggleAI(s.id, e.target.checked)} disabled={!sel.enabled} data-testid={`sec-ai-${s.id}`} />
                            AI
                          </label>
                        )}
                      </div>
                      {isOpen && (
                        <div className="px-8 pb-3 space-y-2 border-t border-border">
                          {allowedRootsFor(s.id).length > 0 && (
                            <div className="pt-2 flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Source:</span>
                              <Badge variant="outline" className="text-[10px]">{currentRootLabelFor(s.id, sel, s)}</Badge>
                              <span className="text-muted-foreground">›</span>
                              <span className="font-medium text-foreground">{currentSubLabelFor(sel, s)}</span>
                              <button
                                type="button"
                                className="text-[10px] text-purple-500 underline hover:text-purple-400 ml-1"
                                onClick={() => { setSourceWizardFilter(s.id); setSourceWizardOpen(true); }}
                                data-testid={`sec-source-open-${s.id}`}
                              >change</button>
                              {sel.sourceModule && sel.sourceModule !== s.module && (
                                <button className="text-[10px] text-muted-foreground underline" onClick={() => setSourceOverride(s.id, s.module)}>reset</button>
                              )}
                            </div>
                          )}
                          {(s.subModules?.length > 0) && (
                            <div className="pt-2 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                              {s.subModules.map(sm => (
                                <label key={sm} className="flex items-center gap-1.5 text-xs text-foreground/80">
                                  <input type="checkbox" checked={!!sel.subModules?.[sm]} onChange={e => toggleSub(s.id, sm, e.target.checked)} data-testid={`sec-sub-${s.id}-${sm}`} />
                                  {sm.replace(/_/g,' ')}
                                </label>
                              ))}
                            </div>
                          )}
                          {/* Slide preview + Iterate-with-Cognara controls */}
                          <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" variant="outline" onClick={() => previewSection(s.id)} data-testid={`sec-preview-${s.id}`}>
                              <FileText className="w-3 h-3 mr-1" />{previewOpen[s.id] ? 'Hide' : 'Preview'} slide
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => previewSection(s.id, true)} title="Re-fetch source data and re-render this slide" data-testid={`sec-refresh-${s.id}`}>
                              <RefreshCw className="w-3 h-3 mr-1" />Refresh
                            </Button>
                            {reportId && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => document.getElementById(`sec-photo-input-${s.id}`)?.click()}
                                data-testid={`sec-photo-upload-${s.id}`}
                                title="Upload one or more photos with captions — shown on this section's slide"
                              >
                                <Camera className="w-3 h-3 mr-1" />Photos
                                {sectionPhotos[s.id]?.length ? <span className="ml-1 text-[10px] font-semibold text-purple-500">·{sectionPhotos[s.id].length}</span> : null}
                              </Button>
                            )}
                            <input
                              id={`sec-photo-input-${s.id}`}
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => uploadSectionPhotos(s.id, Array.from(e.target.files || []))}
                              data-testid={`sec-photo-input-${s.id}`}
                            />
                            <Input
                              className="h-8 text-xs bg-muted border-border flex-1"
                              placeholder='Iterate with Cognara — e.g. "make it more punchy" / "add cashflow angle"'
                              value={iterateInput[s.id] || ''}
                              onChange={e => setIterateInput(x => ({ ...x, [s.id]: e.target.value }))}
                              data-testid={`sec-iterate-input-${s.id}`}
                            />
                            <Button size="sm" onClick={() => iterateSection(s.id)} disabled={iterating[s.id]} className="bg-purple-500 hover:bg-purple-600 text-white" data-testid={`sec-iterate-${s.id}`}>
                              {iterating[s.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}Iterate
                            </Button>
                          </div>
                          {/* Uploaded photos strip with caption editing + delete + drag-reorder + lightbox */}
                          {sectionPhotos[s.id]?.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid={`sec-photos-strip-${s.id}`}>
                              {sectionPhotos[s.id].map((p, idx) => (
                                <div
                                  key={p.photoId}
                                  className="relative rounded-md border border-border overflow-hidden bg-muted/20 cursor-move"
                                  draggable
                                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.photoId); e.dataTransfer.effectAllowed = 'move'; }}
                                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const srcId = e.dataTransfer.getData('text/plain');
                                    if (srcId && srcId !== p.photoId) reorderSectionPhoto(s.id, srcId, p.photoId);
                                  }}
                                  data-testid={`sec-photo-${p.photoId}`}
                                  title="Drag to reorder — click image to preview in carousel"
                                >
                                  <div className="absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono">
                                    {idx + 1}
                                  </div>
                                  <img
                                    src={`${API}/api/v1/projects/${projectId}/reports/${reportId}/sections/${s.id}/photos/${p.photoId}`}
                                    alt={p.caption || p.originalName}
                                    className="w-full h-24 object-cover cursor-zoom-in pointer-events-auto"
                                    onClick={() => openLightbox(s.id, idx)}
                                  />
                                  <Input
                                    className="h-7 text-[11px] border-0 rounded-none bg-background"
                                    placeholder="Add a caption…"
                                    defaultValue={p.caption || ''}
                                    onBlur={(e) => updatePhotoCaption(s.id, p.photoId, e.target.value)}
                                    data-testid={`sec-photo-caption-${p.photoId}`}
                                  />
                                  <button
                                    onClick={() => deleteSectionPhoto(s.id, p.photoId)}
                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 hover:bg-red-600 text-white text-xs flex items-center justify-center"
                                    data-testid={`sec-photo-delete-${p.photoId}`}
                                    title="Delete photo"
                                  >✕</button>
                                </div>
                              ))}
                            </div>
                          )}
                          {previewOpen[s.id] && (
                            <iframe
                              title={`preview-${s.id}`}
                              srcDoc={previewHtml[s.id] || '<p style="padding:20px;font-family:system-ui">Loading preview…</p>'}
                              className="w-full h-[420px] rounded border border-border bg-white"
                              data-testid={`sec-preview-frame-${s.id}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-2 justify-end sticky bottom-2 bg-background/80 backdrop-blur border border-border rounded-lg p-3 pr-3 md:pr-20 max-w-full" data-testid="report-export-bar">
          <Button onClick={generatePPTX} variant="outline" data-testid="gen-pptx"><FileText className="w-4 h-4 mr-1.5" />Generate PPTX (editable)</Button>
          <Button onClick={generateHTML} disabled={genJob && genJob.status !== 'COMPLETED' && genJob.status !== 'FAILED'} className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" data-testid="gen-html">
            {genJob && genJob.status !== 'COMPLETED' && genJob.status !== 'FAILED' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
            Generate Narrated HTML
          </Button>
          {/* Direct PDF export — always visible, no need to wait for HTML to
              finish. Server-side WeasyPrint renders the same deck on-demand. */}
          <Button
            onClick={generatePDF}
            disabled={genJob && genJob.status !== 'COMPLETED' && genJob.status !== 'FAILED'}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="gen-pdf"
          >
            {genJob && genJob.status !== 'COMPLETED' && genJob.status !== 'FAILED' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Generate PDF
          </Button>
          <Button
            onClick={generateDOCX}
            disabled={docxBusy}
            className="bg-[#1E4E8C] hover:bg-[#1a4478] text-white"
            data-testid="gen-docx"
          >
            {docxBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
            Generate Word (.docx)
          </Button>
        </div>

        {/* 16:9 slide-strip navigator — click any thumbnail to jump/preview */}
        {orderedCatalog && (() => {
          const active = (orderedCatalog || []).filter(s => s && selected[s.id]?.enabled);
          if (!active.length) return null;
          return (
            <div className="rounded-xl border border-border bg-muted/20 p-3" data-testid="slide-strip">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deck · {active.length} slides · 16:9 preview</div>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
                {active.map((s, idx) => (
                  <button key={s.id} onClick={() => previewSection(s.id, true)} data-testid={`slide-thumb-${s.id}`}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', s.id); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const src = e.dataTransfer.getData('text/plain');
                      if (!src || src === s.id) return;
                      moveSectionOrder(src, s.id);
                      toast.success('Slide reordered — save preset to persist');
                    }}
                    className="snap-start shrink-0 rounded-lg border-2 border-transparent hover:border-primary hover:shadow-md transition-all group text-left overflow-hidden cursor-grab active:cursor-grabbing"
                    style={{ width: 224, aspectRatio: '16 / 9', background: '#fff', position: 'relative' }}>
                    <div className="absolute inset-0 p-2 flex flex-col justify-between">
                      <div className="text-[9px] font-mono text-muted-foreground">§ {String(idx+1).padStart(2,'0')}</div>
                      <div className="text-[11px] font-semibold leading-tight text-foreground line-clamp-3">{s.label}</div>
                      <div className="flex items-center gap-1">
                        {dataHealth[s.id]?.band && (
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{
                              background: {FRESH:'#10B98118',WARM:'#F59E0B18',STALE:'#EF444418',EMPTY:'#94A3B818'}[dataHealth[s.id].band]||'#94A3B818',
                              color:      {FRESH:'#10B981',   WARM:'#F59E0B',   STALE:'#EF4444',   EMPTY:'#64748B'}[dataHealth[s.id].band]||'#64748B',
                            }}>
                            {dataHealth[s.id].band}
                          </span>
                        )}
                        {selected[s.id]?.aiEnabled && <Sparkles className="w-3 h-3 text-purple-500"/>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {genJob && (
          <Card data-testid="gen-progress">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-foreground flex items-center gap-2">{genJob.status !== 'COMPLETED' && genJob.status !== 'FAILED' && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}{genJob.step || genJob.status}</span>
                <span className="text-purple-500 font-semibold">{Math.round(genJob.percent || 0)}%</span>
              </div>
              <Progress value={genJob.percent || 0} className="h-1.5" />
              {generatedHtml && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button asChild size="sm" variant="outline" data-testid="open-html"><a href={generatedHtml} target="_blank" rel="noreferrer">Open Report <ExternalLink className="w-3 h-3 ml-1" /></a></Button>
                  <Button asChild size="sm" variant="outline" data-testid="download-html"><a href={generatedHtml} download><Download className="w-3 h-3 mr-1" />Download HTML</a></Button>
                  <Button asChild size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid="download-pdf"><a href={generatedHtml.replace('/download-html','/download-pdf-v2')} download><Download className="w-3 h-3 mr-1" />Download PDF</a></Button>
                  <span className="text-[10px] text-muted-foreground/70">Or Cmd/Ctrl-P inside the HTML report</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Configure Sources Wizard (modal) ────────────────────────── */}
        <Dialog open={sourceWizardOpen} onOpenChange={async (open) => {
          setSourceWizardOpen(open);
          if (open && reportId) {
            // Refresh narration status so pills reflect the current state
            try {
              const r = await api.get(`/v1/projects/${projectId}/reports/${reportId}`);
              const narr = r.data?.aiNarratives || {};
              const status = {};
              Object.entries(narr).forEach(([sid, n]) => {
                if (n && typeof n === 'object' && (n.insight || (n.recommendations && n.recommendations.length) || n.headline || n.narrative)) {
                  status[sid] = 'AI';
                }
              });
              setNarrativeStatus(status);
            } catch {}
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="source-wizard">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" />Configure Section Sources</DialogTitle>
              <p className="text-xs text-muted-foreground">Pick which frontend module(s) power each section. Root dropdown lists <b>every platform module</b>; hold Ctrl / Cmd in the Sub-Module list to <b>multi-select</b> — data from all picked modules will be merged before rendering. Bulk actions below apply in one click.</p>
            </DialogHeader>
            {/* Bulk-assign quick actions */}
            <div className="flex items-center gap-2 flex-wrap py-2 border-b border-border">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Bulk assign:</span>
              {[
                { label:'All Cost → CBS Matrix',       target:'COST_CBS',        match:(s)=>s.module==='COST_CONTROL' || s.module==='COST_MGMT' || s.module==='COST_CBS' },
                { label:'All Cost → EVM',              target:'COST_MGMT',       match:(s)=>s.module==='COST_CONTROL' || s.module==='COST_MGMT' || s.module==='COST_CBS' },
                { label:'All Schedule → Macro',        target:'MACRO_SCHEDULE',  match:(s)=>s.module==='SCHEDULE_MGMT' || s.module==='MACRO_SCHEDULE' || s.module==='SCHEDULE_ROLLUP' },
                { label:'All Narrative → Cognara',     target:'NARRATIVE',       match:(s)=>s.module==='NARRATIVE' },
                { label:'All Workfront → Piping',      target:'PIPING_WORKFRONT',match:(s)=>s.module==='PIPING_WORKFRONT' || s.module==='STRUCTURAL_WORKFRONT' || s.module==='FC_ACTIVITIES' },
              ].map(action => (
                <Button
                  key={action.label}
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  data-testid={`sw-bulk-${action.target}`}
                  onClick={() => {
                    let count = 0;
                    setSelected(p => {
                      const next = { ...p };
                      catalog.forEach(s => {
                        if (action.match(s)) {
                          next[s.id] = { ...(next[s.id] || {}), sourceModule: action.target };
                          count++;
                        }
                      });
                      return next;
                    });
                    toast.success(`Applied "${action.label}" to ${count} section(s)`);
                  }}
                >{action.label}</Button>
              ))}
            </div>
            <div className="flex items-center gap-2 py-2">
              <Input
                className="h-8 text-xs flex-1"
                placeholder="Filter by section name…"
                value={sourceWizardFilter}
                onChange={e => setSourceWizardFilter(e.target.value)}
                data-testid="source-wizard-filter"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                data-testid="source-wizard-reset-all"
                onClick={() => {
                  setSelected(p => {
                    const next = { ...p };
                    Object.keys(next).forEach(id => { if (next[id]) next[id] = { ...next[id], sourceModule: undefined }; });
                    return next;
                  });
                  toast.success('All sources reset to defaults');
                }}
              >Reset All</Button>
            </div>
            <div className="flex-1 overflow-y-auto pr-1 -mr-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background border-b border-border">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-2 w-[36%]">Section</th>
                    <th className="py-2 pr-2 w-[52%]">Module → Sub-Module (platform view)</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {catalog
                    .filter(s => !sourceWizardFilter
                      || s.id.toLowerCase().includes(sourceWizardFilter.toLowerCase())
                      || s.label.toLowerCase().includes(sourceWizardFilter.toLowerCase()))
                    .map(s => {
                      const sel = selected[s.id] || {};
                      const roots = allowedRootsFor(s.id);
                      const currentRoot = currentRootLabelFor(s.id, sel, s);
                      const rootObj = PLATFORM_MODULES.find(m => m.root === currentRoot);
                      const currentV = sel.sourceModule || s.module;
                      const isOverridden = sel.sourceModule && sel.sourceModule !== s.module;
                      return (
                        <tr key={s.id} className="border-b border-border/40 hover:bg-muted/30 align-top">
                          <td className="py-2 pr-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={!!sel.enabled}
                                onChange={e => toggleSection(s.id, e.target.checked)}
                                data-testid={`sw-toggle-${s.id}`}
                              />
                              {(() => {
                                const rIcon = PLATFORM_MODULES.find(m => m.root === currentRoot);
                                const RIcon = rIcon?.Icon;
                                return RIcon ? <RIcon className="w-3.5 h-3.5" style={{color: rIcon.color}} /> : null;
                              })()}
                              <div>
                                <div className="font-medium text-foreground">{s.label}</div>
                                <div className="text-[10px] text-muted-foreground">Default: {s.module.replace(/_/g,' ')}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-2">
                            {roots.length > 0 ? (
                              <div className="grid grid-cols-2 gap-1.5">
                                <select
                                  className="h-7 text-xs bg-muted border border-border rounded px-2"
                                  value={currentRoot}
                                  onChange={e => {
                                    const nr = PLATFORM_MODULES.find(m => m.root === e.target.value);
                                    if (nr?.subs?.[0]) setSourceMulti(s.id, [nr.subs[0].v]);
                                  }}
                                  data-testid={`sw-root-${s.id}`}
                                  disabled={!sel.enabled}
                                >
                                  {roots.map(r => (<option key={r} value={r}>{r}</option>))}
                                </select>
                                <select
                                  multiple
                                  size={Math.min(6, Math.max(2, (rootObj?.subs || []).length))}
                                  className="text-xs bg-muted border border-border rounded px-2 py-1"
                                  value={sel.sources || [currentV]}
                                  onChange={e => {
                                    const picked = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setSourceMulti(s.id, picked.length ? picked : [rootObj?.subs?.[0]?.v || s.module]);
                                  }}
                                  data-testid={`sw-sub-${s.id}`}
                                  disabled={!sel.enabled || !rootObj}
                                  title="Ctrl / Cmd-click to pick multiple sources"
                                >
                                  {(rootObj?.subs || []).map((sub, i) => (
                                    <option key={`${sub.v}-${i}`} value={sub.v}>{sub.label}</option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <span className="text-[11px] text-muted-foreground italic">No alternatives — uses {s.module.replace(/_/g,' ')}</span>
                            )}
                            {(sel.sources?.length || 0) > 1 && (
                              <div className="text-[9px] text-purple-500 mt-1">Multi-source · {sel.sources.length} modules will be merged</div>
                            )}
                          </td>
                          <td className="py-2">
                            <div className="flex flex-col gap-1">
                              {isOverridden ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-500">● OVERRIDDEN</span>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">default</span>
                              )}
                              {sel.enabled && (
                                narrativeStatus[s.id] === 'AI' ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-500 bg-purple-500/10 px-1.5 py-0.5 rounded" title="Cognara has authored this section">
                                    <Sparkles className="w-2.5 h-2.5" />AI-authored ✦
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-500/10 px-1.5 py-0.5 rounded" title="Deterministic fallback — click Author All to generate Cognara narration">
                                    Deterministic
                                  </span>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <DialogFooter className="pt-3 border-t border-border">
              <div className="flex items-center justify-between w-full">
                <span className="text-[11px] text-muted-foreground">
                  {Object.values(selected).filter(x => x?.enabled && x?.sourceModule).length} section(s) with a source override
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSourceWizardOpen(false)} data-testid="source-wizard-close" disabled={applyingSources}>Close</Button>
                  <Button
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600 text-white min-w-[220px]"
                    data-testid="source-wizard-apply"
                    disabled={applyingSources}
                    onClick={async () => {
                      // Regenerate previews for every enabled section with a source override
                      const overridden = catalog.filter(s => selected[s.id]?.enabled && selected[s.id]?.sourceModule && selected[s.id].sourceModule !== s.module);
                      if (!overridden.length) {
                        setSourceWizardOpen(false);
                        toast.info('No source overrides to apply');
                        return;
                      }
                      setApplyingSources({ total: overridden.length, done: 0, current: '' });
                      // Ensure a report exists so preview endpoint can persist the new sourceModule
                      let id = reportId;
                      if (!id) id = await createOrGetReport();
                      if (!id) { setApplyingSources(null); return; }
                      for (let i = 0; i < overridden.length; i++) {
                        const s = overridden[i];
                        setApplyingSources({ total: overridden.length, done: i, current: s.label });
                        try {
                          await api.get(`/v1/projects/${projectId}/reports/${id}/sections/${s.id}/preview?ts=${Date.now()}`);
                        } catch {}
                      }
                      setPreviewHtml({});
                      setApplyingSources(null);
                      setSourceWizardOpen(false);
                      toast.success(`Rebuilt ${overridden.length} slide(s) with new sources`);
                    }}
                  >
                    {applyingSources ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Rebuilding {applyingSources.done+1}/{applyingSources.total}…</>) : ('Apply & Refresh Previews')}
                  </Button>
                </div>
              </div>
              {applyingSources && (
                <div className="w-full mt-2">
                  <Progress value={Math.round((applyingSources.done / Math.max(applyingSources.total,1)) * 100)} className="h-1" />
                  <div className="text-[10px] text-muted-foreground mt-1 truncate">Currently: {applyingSources.current}</div>
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══ Photo Lightbox / Carousel Preview ═══ */}
        {lightbox && (() => {
          const list = sectionPhotos[lightbox.sectionId] || [];
          const p = list[lightbox.index];
          if (!p) return null;
          return (
            <div
              className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
              onClick={closeLightbox}
              data-testid="photo-lightbox"
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeLightbox();
                if (e.key === 'ArrowRight') stepLightbox(1);
                if (e.key === 'ArrowLeft') stepLightbox(-1);
              }}
              tabIndex={0}
            >
              <button
                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white text-xl flex items-center justify-center"
                data-testid="lightbox-close"
                title="Close (Esc)"
              >✕</button>
              {list.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center"
                    data-testid="lightbox-prev"
                    title="Previous (←)"
                  >‹</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center"
                    data-testid="lightbox-next"
                    title="Next (→)"
                  >›</button>
                </>
              )}
              <div
                className="relative flex flex-col items-center gap-4 max-w-[92vw] max-h-[92vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={`${API}/api/v1/projects/${projectId}/reports/${reportId}/sections/${lightbox.sectionId}/photos/${p.photoId}`}
                  alt={p.caption || p.originalName}
                  className="max-w-full max-h-[75vh] rounded-lg shadow-2xl object-contain bg-neutral-900"
                  data-testid="lightbox-image"
                />
                <div className="bg-black/40 backdrop-blur px-4 py-2 rounded-lg text-white max-w-[80vw] text-center">
                  <div className="text-sm font-medium">{p.caption || <span className="italic text-white/60">No caption</span>}</div>
                  <div className="text-[11px] text-white/60 mt-0.5">
                    {p.originalName} · {lightbox.index + 1} / {list.length}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </DashboardLayout>
  );
}
